import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Tooltip } from "antd";
import { CheckCircleOutlined, MessageOutlined, RedoOutlined } from "@ant-design/icons";
import Layout from '../../../components/Layout';
import { post } from '../../../services/ApiService';
import "../../custom-style.css";
import Loader from "../../../components/Loader";
import Pagination from '../../../components/Pagination';
import Breadcrumbs from "../../../components/Breadcrumbs";
import DataTable from "../SignPost/DataTable";
import { DateTimeFormater, truncateText } from "../../../services/Helper";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { ESCALATION_TYPES, resolvedLabelFor, countReplies } from "./escalationTypes";
import useEscalationActions from "./useEscalationActions";

// Orders escalated from the order detail page, one panel per escalation type
// (see escalationTypes.js). Opens on OPEN escalations; resolving one moves it to RESOLVED.
const DEFAULT_FILTERS = {
    orderId: '',
    customer: '',
    escalationStatus: 'OPEN',
    limit: '20',
    sortOrder: 'newest',
};

const escalationColumns = ({ resolvedStatusLabel }) => [
    {
        key: 'escalationReason',
        header: 'Escalation Reason',
        className: 'order-panel-wide',
        render: (order) => (
            <span style={{ fontSize: '12px', whiteSpace: 'normal' }} title={order.escalation?.reason || ''}>
                {truncateText(order.escalation?.reason || '—', 80)}
            </span>
        ),
    },
    {
        key: 'escalatedBy',
        header: 'Escalated By',
        className: 'order-panel-nowrap',
        render: (order) => (
            <span style={{ fontSize: '12px' }}>
                {order.escalation?.escalatedBy || '—'}
                <br />
                <span className="text-muted">{DateTimeFormater(order.escalation?.escalatedAt)}</span>
            </span>
        ),
    },
    {
        key: 'escalationStatus',
        header: 'Escalation',
        className: 'order-panel-nowrap',
        render: (order) => {
            const escalation = order.escalation || {};
            if (escalation.status !== 'RESOLVED') {
                return <span className="badge rounded-pill bg-danger">OPEN</span>;
            }
            return (
                <span style={{ fontSize: '12px' }} title={escalation.resolutionNote || ''}>
                    <span className="badge rounded-pill bg-success">{resolvedStatusLabel}</span>
                    <br />
                    <span className="text-muted">
                        {escalation.resolvedBy || '—'} · {DateTimeFormater(escalation.resolvedAt)}
                    </span>
                </span>
            );
        },
    },
];

const EscalatedOrders = ({ type = 'REFUND' }) => {

    const config = ESCALATION_TYPES[type];
    const canResolve = usePermissionCheck([config.resolvePermission]);
    const resolvedLabel = resolvedLabelFor(config);

    const [isLoading, setIsLoading] = useState(true);
    const [isNotFound, setNotFound] = useState(false);
    const [orders, setOrders] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            const res = await post(config.api.list, { page: currentPage, ...filters });
            if (res.success) {
                setOrders(res.orders);
                setTotalPages(res.totalPages);
                setNotFound(false);
            } else {
                setOrders([]);
                setTotalPages(1);
                setNotFound(true);
            }
        } catch (error) {
            setOrders([]);
            setTotalPages(1);
            setNotFound(true);
            console.error(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [config, currentPage, filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFilterChange = (e) => {
        setCurrentPage(1);
        setFilters({
            ...filters,
            [e.target.name]: e.target.value
        });
    };

    const resetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setCurrentPage(1);
    };

    // A status change can move the row out of the current filter, so reload;
    // a reply only updates the row in place.
    const escalationActions = useEscalationActions({
        onStatusChange: fetchData,
        onReply: (escalation) => setOrders((rows) => rows.map((row) => (
            row.escalation?._id === escalation._id ? { ...row, escalation } : row
        ))),
    });

    const renderActions = (order) => {
        const { escalation, orderId } = order;
        const status = escalation?.status;
        const replyCount = countReplies(escalation);
        return (
            <div className="order-panel-icon-actions">
                {canResolve && status === 'OPEN' && (
                    <Tooltip title={`${config.resolveLabel} escalation`}>
                        <Button
                            className="order-panel-primary-btn"
                            icon={<CheckCircleOutlined />}
                            aria-label={`${config.resolveLabel} escalation`}
                            onClick={() => escalationActions.openResolve(escalation, orderId)}
                        />
                    </Tooltip>
                )}
                {canResolve && status === 'RESOLVED' && (
                    <Tooltip title="Reopen escalation">
                        <Button
                            icon={<RedoOutlined />}
                            aria-label="Reopen escalation"
                            onClick={() => escalationActions.openReopen(escalation, orderId)}
                        />
                    </Tooltip>
                )}
                <Tooltip title={`Replies (${replyCount})`}>
                    <Badge count={replyCount} size="small" color="var(--bs-primary)">
                        <Button
                            icon={<MessageOutlined />}
                            aria-label={`Replies (${replyCount})`}
                            onClick={() => escalationActions.openActivity(escalation, orderId)}
                        />
                    </Badge>
                </Tooltip>
            </div>
        );
    };

  return (
    <>
        <Layout>
            <div className="row mb-9 align-items-center justify-content-between">
                <div className="col-sm-6 mb-8 mb-sm-0">
                <h2 className="fs-4 mb-0">{config.title}</h2>
                <Breadcrumbs />
                </div>
            </div>
            <div className="card mb-4 rounded-4 p-7">
                <div className="card-header bg-transparent px-0 pt-0 pb-7">
                <div className="row align-items-center justify-content-between mb-4">
                    <div className="col-lg-3 col-12 col-sm-12 col-md-6 mr-auto mb-md-0 mb-6">
                        <input type="text" placeholder="Search by order id..." name="orderId" value={filters.orderId} onChange={handleFilterChange} className="form-control bg-input border-0" />
                    </div>
                    <div className="col-lg-3 col-12 col-md-6 col-sm-12 mr-auto mb-md-0 mb-6">
                        <input type="text" placeholder="Search by customer..." name="customer" value={filters.customer} onChange={handleFilterChange} className="form-control bg-input border-0" />
                    </div>
                    <div className="col-lg-3 col-md-6 col-12 col-sm-12 mr-auto mb-md-0 mb-6">
                        <select className="form-select" name="escalationStatus" value={filters.escalationStatus} onChange={handleFilterChange}>
                            <option value="OPEN">Open escalations</option>
                            <option value="RESOLVED">{resolvedLabel} escalations</option>
                            <option value="All">All escalations</option>
                        </select>
                    </div>
                    <div className="col-lg-3 col-md-6 col-12 col-sm-12 mr-auto mb-md-0 mb-6">
                        <select className="form-select" name="limit" value={filters.limit} onChange={handleFilterChange}>
                            <option value="10">Show 10</option>
                            <option value="20">Show 20</option>
                            <option value="50">Show 50</option>
                            <option value="100">Show 100</option>
                        </select>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                        <select className="form-select" name="sortOrder" value={filters.sortOrder} onChange={handleFilterChange}>
                            <option value="newest">Newest to Oldest</option>
                            <option value="oldest">Oldest to Newest</option>
                        </select>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                        <Link className="mt-4 text-danger" onClick={resetFilters}><i className="fa fa-sync" style={{fontSize:'20px',marginTop:'15px'}}></i></Link>
                    </div>
                </div>
                </div>
                <div className="card-body px-0 pt-7 pb-0">
                    {isLoading ? (
                        <div className="text-center">
                            <Loader />
                        </div>
                    ) : (
                        orders.length > 0 && (
                            // Scroll the table, not the page, on narrow screens like the other order lists.
                            <div className="table-responsive">
                                <DataTable orders={orders} extraColumns={escalationColumns(config)} renderActions={renderActions} />
                            </div>
                        )
                    )}
                    {!isLoading && isNotFound &&
                        <div className="text-center">
                        <img src="/assets/images/no-data.png" alt="" style={{width:'60px',opacity:'0.5'}} />
                        <h6 style={{color:'#918f8f',fontFamily: 'math',fontWeight: '500'}}>No Data Found</h6>
                        </div>
                    }
                </div>
            </div>
            {totalPages > 1 &&
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            }
            {escalationActions.modals}
        </Layout>
    </>
  )
}

export default EscalatedOrders
